import { useState, useEffect } from 'react'
import { Calendar, PartyPopper, Sun } from 'lucide-react'
import { getFunctions, httpsCallable } from 'firebase/functions'

interface HolidayInfo {
  name: string
  date: string
  dayOfWeek: string
  location: string
  countryCode: string
  stateCode: string | null
  memberCount: number
  isToday: boolean
  isThisWeek: boolean
  isNextWeek: boolean
}

interface UpcomingHolidays {
  today: HolidayInfo[]
  thisWeek: HolidayInfo[]
  nextWeek: HolidayInfo[]
}

// Map country codes to friendly names
const countryNames: Record<string, string> = {
  NZ: 'New Zealand',
  AU: 'Australia',
  PH: 'Philippines',
  US: 'United States',
  GB: 'United Kingdom',
  JP: 'Japan',
}

// Map state codes to friendly names for AU
const auStateNames: Record<string, string> = {
  VIC: 'VIC',
  NSW: 'NSW',
  QLD: 'QLD',
  WA: 'WA',
  SA: 'SA',
  TAS: 'TAS',
  NT: 'NT',
  ACT: 'ACT',
}

function formatLocationName(countryCode: string, stateCode: string | null): string {
  if (stateCode && countryCode === 'AU') {
    return auStateNames[stateCode] || stateCode
  }
  return countryNames[countryCode] || countryCode
}

function formatHolidayMessage(holiday: HolidayInfo): string {
  const location = formatLocationName(holiday.countryCode, holiday.stateCode)
  
  if (holiday.isToday) {
    // "Enjoy the Waitangi Day today, NZ team!"
    return `Enjoy ${holiday.name} today, ${location} team!`
  } else if (holiday.isThisWeek) {
    // "Waitangi Day in NZ on Friday this week"
    return `${holiday.name} in ${location} on ${holiday.dayOfWeek} this week`
  } else {
    // "Waitangi Day in NZ next week"
    return `${holiday.name} in ${location} next week (${holiday.dayOfWeek})`
  }
}

export const HolidayBanner: React.FC = () => {
  const [holidays, setHolidays] = useState<UpcomingHolidays | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        setLoading(true)
        const functions = getFunctions()
        const getUpcomingHolidays = httpsCallable(functions, 'getUpcomingHolidays')
        const result = await getUpcomingHolidays()
        const data = result.data as { success: boolean; data: UpcomingHolidays }
        
        if (data.success) {
          setHolidays(data.data)
        }
      } catch (err) {
        console.error('Error fetching holidays:', err)
        setError('Failed to load holidays')
      } finally {
        setLoading(false)
      }
    }

    fetchHolidays()
  }, [])

  // Don't show anything while loading or if there's an error
  if (loading || error || !holidays) {
    return null
  }

  // Check if there are any holidays to show
  const hasHolidays = holidays.today.length > 0 || 
                      holidays.thisWeek.length > 0 || 
                      holidays.nextWeek.length > 0

  if (!hasHolidays) {
    return null
  }

  // Combine all holidays and deduplicate by name + date
  const allHolidays: HolidayInfo[] = []
  const seen = new Set<string>()

  // Today's holidays take priority
  for (const h of holidays.today) {
    const key = `${h.name}-${h.date}`
    if (!seen.has(key)) {
      seen.add(key)
      allHolidays.push(h)
    }
  }

  // Then this week's
  for (const h of holidays.thisWeek) {
    const key = `${h.name}-${h.date}`
    if (!seen.has(key)) {
      seen.add(key)
      allHolidays.push(h)
    }
  }

  // Then next week's
  for (const h of holidays.nextWeek) {
    const key = `${h.name}-${h.date}`
    if (!seen.has(key)) {
      seen.add(key)
      allHolidays.push(h)
    }
  }

  // Limit to 3 holidays to avoid cluttering
  const displayHolidays = allHolidays.slice(0, 3)

  // Determine banner style based on if there's a holiday today
  const hasTodayHoliday = holidays.today.length > 0

  return (
    <div className={`rounded-lg px-3 py-1.5 mb-4 ${
      hasTodayHoliday 
        ? 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200' 
        : 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200'
    }`}>
      <div className="flex items-center gap-2">
        {hasTodayHoliday ? (
          <div className="flex-shrink-0 p-1.5 bg-amber-100 rounded-full">
            <PartyPopper className="w-4 h-4 text-amber-600" />
          </div>
        ) : (
          <div className="flex-shrink-0 p-1.5 bg-blue-100 rounded-full">
            <Calendar className="w-4 h-4 text-blue-600" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {displayHolidays.map((holiday, index) => (
              <span 
                key={`${holiday.name}-${holiday.date}-${holiday.location}`}
                className={`text-sm ${
                  holiday.isToday 
                    ? 'text-amber-800 font-medium' 
                    : 'text-gray-700'
                }`}
              >
                {holiday.isToday && <Sun className="w-3.5 h-3.5 inline mr-1 text-amber-500" />}
                {formatHolidayMessage(holiday)}
                {index < displayHolidays.length - 1 && (
                  <span className="text-gray-400 ml-4">•</span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
